<template>
  <EditorBlockView
    :block-id="page.editorBlock.id"
    :content="page.editorBlock.content"
    :context="Context.DsrPublished"
    :sender="owner"
    :theme="entity.theme"
    :recipient="participant"
    :merge-values="mergeValues"
    :recipient-input="participantInput"
    :pusher-client="pusherClient"
    :show-contextual-comments="showContextualCommenting"
    :create-contextual-comment-button-state="createContextualCommentButtonState"
    :selected-comment-id="selectedCommentId"
    :comments="filteredComments"
    :apollo-client="apolloClient"
    :all-content="allContent"
    @node-click="postClickTracking({ ...$event, editorBlockId: page.editorBlock.id })"
    @observe-section-visibility="
      observeSectionVisibility({
        ...$event,
        editorBlockId: page.editorBlock.id,
      })
    "
    @observe-node-visibility="
      observeNodeVisibility({ ...$event, editorBlockId: page.editorBlock.id })
    "
    @update-recipient-input="$emit('update-participant-input', $event)"
    @fetch-new-image="$emit('fetch-new-image', $event)"
    @create-comment="
      $event =>
        handleCreateComment({
          ...$event,
          editorBlockVersion: page.editorBlock.contentVersion,
          editorBlockId: page.editorBlock.id,
        })
    "
    @select-comment="handleSelectComment"
  />
</template>

<script lang="ts">
import type { DealroomPublished, Page, Participant } from '@getaccept/dsr-shared-new';
import { useContextualCommentingStore } from '@getaccept/dsr-shared-new/src/contextual-commenting/store/contextual-commenting.store';
import type { CommentEditorSelection } from '@getaccept/dsr-shared-new/src/contextual-commenting/types/comment-selection';
import { CreateContextualCommentButtonState } from '@getaccept/dsr-shared-new/src/contextual-commenting/types/create-contextual-comment-button-state';
import type { DocumentValues, MergeValues } from '@getaccept/editor-lib-new';
import { Context } from '@getaccept/editor-lib-new';
import { EditorBlockView } from '@getaccept/editor-view-new';
import type { Entity } from '@getaccept/lib-shared-new/src/entity/signing-site/types/entity';
import type { EditorRecipientInput } from '@getaccept/lib-shared-new/src/types/editor-recipient-input';
import { storeToRefs } from 'pinia';
import type { ComputedRef, PropType } from 'vue';
import { computed, defineComponent, watch } from 'vue';
import { useRoute } from 'vue-router';
import { pusherClient } from '../../../api/pusher/pusher-client';
import { useContextualCommentingData } from '../../../contextual-commenting/composables/contextual-commenting-data.composable';
import { useParticipantsStore } from '../../store/participants.store';
import { useTracking } from '../tracking/composables/tracking.composable';
import { apolloClient } from '../../../apollo';

export default defineComponent({
  name: 'EditorWrapper',
  components: {
    EditorBlockView,
  },
  props: {
    page: {
      required: true,
      type: Object as PropType<Page>,
    },
    entity: {
      default: () => ({}),
      type: Object as PropType<Entity>,
    },
    participantInput: {
      default: () => [],
      type: Array as PropType<EditorRecipientInput[]>,
    },
    dsr: {
      required: true,
      type: Object as PropType<DealroomPublished>,
    },
    authId: {
      required: true,
      type: String,
    },
    participant: {
      default: () => ({}),
      type: Object as PropType<Participant>,
    },
    pages: {
      type: Array as PropType<Page[]>,
      default: () => [],
    },
    token: {
      required: true,
      type: Object as PropType<{
        jwt: string;
        exp: number;
      }>,
    },
    showContextualCommenting: {
      type: Boolean,
    },
    commentingLocked: {
      type: Boolean,
    },
  },
  setup(props) {
    const participantsStore = useParticipantsStore();
    const { participants, owner } = storeToRefs(participantsStore);

    const {
      observeNodeVisibility,
      observeSectionVisibility,
      postClickTracking,
      stopNodeTrackings,
    } = useTracking(props.pages);

    const documentValues: ComputedRef<DocumentValues> = computed(() => {
      const { name } = props.dsr || {};
      const { value, uniqueId, expirationDate, companyName } = props.dsr.details || {};
      return { name, value, uniqueId, expirationDate, companyName };
    });

    const mergeValues: ComputedRef<MergeValues> = computed(() => ({
      document: documentValues.value,
      sender: owner.value,
      recipients: participants.value,
      entity: props.entity,
    }));

    const route = useRoute();

    const isLightboxOpen = computed(() => !!route.query?.lightbox);

    watch(isLightboxOpen, () => {
      if (isLightboxOpen.value) {
        stopNodeTrackings();
      }
    });

    const ccStore = useContextualCommentingStore();
    const { selectedCommentId, viewableComments } = storeToRefs(ccStore);

    const filteredComments = computed(() =>
      props.showContextualCommenting
        ? viewableComments.value.filter(
            comment => comment.editorSelection.editorBlockId === props.page.editorBlock.id
          )
        : []
    );

    const createContextualCommentButtonState = computed(() =>
      props.commentingLocked
        ? CreateContextualCommentButtonState.SenderHasMadeChanges
        : CreateContextualCommentButtonState.Enabled
    );

    const { selectComment, createComment } = useContextualCommentingData();

    const handleSelectComment = (commentId: string) => {
      selectComment(commentId, props.participant?.id);
    };

    const handleCreateComment = (editorSelection: CommentEditorSelection) => {
      createComment(editorSelection, props.participant.id);
    };

    const allContent = computed(() =>
      props.pages.filter(page => page.editorBlock).map(page => page.editorBlock.content)
    );

    return {
      observeSectionVisibility,
      observeNodeVisibility,
      postClickTracking,
      selectComment,
      handleSelectComment,
      handleCreateComment,
      mergeValues,
      owner,
      pusherClient,
      createContextualCommentButtonState,
      selectedCommentId,
      filteredComments,
      Context,
      apolloClient,
      allContent,
    };
  },
});
</script>
